import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { GraduationCap, BadgeCheck } from 'lucide-react';
import { AuthPanel } from '../components/AuthPanel';
import { Button } from '../components/ui';
import { auth } from '../firebase';
import { authService } from '../services/auth';

type AuthMode = 'signin' | 'signup' | 'forgot';

export const DpsSignInPage: React.FC = () => {
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [user, setUser] = useState<User | null>(() => authService.getCurrentUser());

  const openAuth = (mode: AuthMode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  React.useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">DPS School Portal</div>
              <div className="text-sm text-gray-600">mAIthZen access for DPS students</div>
            </div>
          </div>
          {user ? (
            <Button variant="secondary" onClick={() => navigate('/home')}>
              Open App
            </Button>
          ) : (
            <Button variant="primary" onClick={() => openAuth('signin')}>
              Sign in
            </Button>
          )}
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 leading-tight">
              Welcome to DPS mAIthZen
            </h1>
            <p className="mt-4 text-lg text-gray-700">
              Sign in with your DPS account to continue.
            </p>

            <div className="mt-8 space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-1 w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <BadgeCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Verified school access</div>
                  <div className="text-sm text-gray-600">Only DPS users can sign in on this portal.</div>
                </div>
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              <Button variant="primary" size="lg" onClick={() => openAuth('signin')}>
                Sign in
              </Button>
              <Button variant="secondary" size="lg" onClick={() => openAuth('forgot')}>
                Forgot password
              </Button>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur rounded-2xl border border-indigo-100 shadow-sm p-8">
            <div className="text-sm font-semibold text-gray-900">DPS School Login</div>
            <div className="mt-2 text-sm text-gray-600">
              Click “Sign in” to access mAIthZen.
            </div>
            <div className="mt-6">
              <Button fullWidth variant="primary" onClick={() => openAuth('signin')}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AuthPanel isOpen={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </div>
  );
};
